import React from "react";
import { connect } from "react-redux";
import { Input, Select, Switch } from "../../Input/index.js";
import Separator from "../../Separator/Separator.js";
import language from "../../../language/index.js";
import icons from "../../../constants/icons.js";
import { Button } from "../../Button/index.js";
import toastMessage from "../../../utils/toastMessage.js";
import { fetchBloodBanks } from "../../../utils/queries/bloodBank.js";
import hospitalQuery from "../../../utils/queries/hospitalQuery.js";
import { getStorage } from "../../../utils/storage.js";
import ShippingQueue from "./ShippingQueue.js";
import { fetchShipmentNumber } from "../../../utils/queries/request/shipping-queue.js";
import { generateShipmentNumber } from "../../../utils/random.js";
import { defineRoleCenterSite } from "../../../utils/handleAccessRoles.js";
import { Modal } from "../../Modal/index.js";
import PrintableLabel from "../../Production/ComponentLabeling/printableLabel/index.js";
import api from "../../../utils/api/index.js";
import { fetchInventories } from "../../../utils/queries/production/inventoryQuery.js";
import bloodDonationQuery from "../../../utils/queries/bloodDonationQuery";
import Loading from "../../Loader/Loading.js";
import departmentQuery from "../../../utils/queries/departmentQuery.js";
import { fetchPatientHospitalizations } from "../../../utils/queries/patient-hospitalization.js";
import EmergencyIssueCheck from "../emergency/EmergencyIssueCheck";
import { skipsCrossmatch } from "../emergency/helpers";

const CROSSMATCH_PHASES = [
  { id: "IS",   abbr: "IS",   label: "Immediate Spin",            passLabel: "Non-Reactive", failLabel: "Reactive",  description: "Detects ABO / IgM incompatibilities at room temperature. Must be Non-Reactive to continue." },
  { id: "LISS", abbr: "Liss/Incubation at 37°C", label: "",       passLabel: "Non-Reactive", failLabel: "Reactive",  description: "Low Ionic Strength Saline — enhances IgG antibody detection. Mandatory after IS Non-Reactive." },
  { id: "IAT",  abbr: "IAT",  label: "Indirect Antiglobulin Test", passLabel: "Non-Reactive", failLabel: "Reactive",  description: "Detects clinically significant IgG antibodies. Must be Non-Reactive to proceed." },
  { id: "CC",   abbr: "CC",   label: "Check Cells",               passLabel: "Positive",     failLabel: "Negative",  description: "Validates IAT technique. Positive confirms the IAT was performed correctly — product is COMPATIBLE." },
];

class DirectTransfer extends React.Component {
  productInputRef = React.createRef();
  donationInputRef = React.createRef();

  state = {
    error: {},
    data: [],
    isDoneSearching: false,
    donationNumber: "",
    donation: {},
    inventories: [],
    sourceStocks: [],
    destinationStocks: [],
    serviceStocks: [],
    isHospitalSource: false,
    nominativeDestination: null,
    inventory: "",
    product: "",
    user: {},
    isLoadingShipmentNumber: true,
    isCompatibleWithPatient: false,
    isRedBloodCell: null,
    isFetchingProductType: false,
    crossmatchPhases: { IS: null, LISS: null, IAT: null, CC: null },
    crossmatchVerdict: null,
    crossmatchCurrentPhaseIndex: 0,
    isCheckingBloodGroup: false,
    bloodGroupStatus: null,
    bloodGroupMessage: "",
    // The unit actually scanned, so an emergency release that waived
    // crossmatching can still say whether the bag matches the patient.
    scannedUnit: null,

    bloodBank: null,
    shipmentNumber: "",
    source: null,
    showReservationModal: false,
    reservationInfo: null,
    pendingAction: null,
    isCancellingReservation: false,
    showGuide: false,
};

  componentDidMount = async () => {
    const user = await getStorage();
    let source;

    if (this.props._id) {
      this.setState({
        shipmentNumber: this.props.shipmentNumber,
        donation: this.props.donation
          ? {
              ...this.props.donation,
              label: this.props.donation.donationNumber,
              value: this.props.donation._id,
            }
          : null,
        source: this.props.from
          ? {
              label: this.props.from.code + " " + this.props.from.name,
              value: this.props.from._id,
            }
          : null,
        bloodBank: this.props.to
          ? {
              label: this.props.to.code + " " + this.props.to.name,
              value: this.props.to._id,
            }
          : null,
        status: this.props.status
          ? {
              label: this.props.status,
              value: this.props.status,
            }
          : null,
      });
    } else {
      this.getShipmentNumber(true);
      this.setState({
        source: this.props.from || null,
        bloodBank: this.props.to || null,
      });
    }

    if (this.props.isForNominative && this.props.hospital?._id) {
      // Source = the hospital blood bank (physical origin of blood); destination = requesting ward/service
      source = { label: this.props.hospital?.name, value: null };
      this.loadAllowedDestinationsFromHospital(this.props.hospital._id);
      this.setState({ user, source });
    } else if (user.stock && user.stock !== "") {
      source = {
        label: user.stockName,
        value: user.stock,
      };
      this.setState({ user, source });
      // Probe destinations: if blood banks returns empty, this is a hospital lab → show services
      const destinations = await this.getDestinationBloodBanks(true);
      const isHospitalSource = !destinations || destinations.length === 0;
      if (isHospitalSource) {
        await this.getPatientServices(true);
      }
      this.setState({ isHospitalSource });
    } else {
      this.getBloodBanks(true);
      this.setState({ user, source });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.refreshShipmentNumber !== this.props.refreshShipmentNumber &&
      this.props.refreshShipmentNumber
    ) {
      this.getShipmentNumber(true);
    }

    if (this.props.isForNominative) {
      const donationJustCompleted =
        prevState.donationNumber.length < 7 && this.state.donationNumber.length >= 7;
      const productReady = this.state.product.length >= 5;

      // Re-run product type detection when donation number completes and type still unknown
      if (
        donationJustCompleted &&
        productReady &&
        this.state.isRedBloodCell === null &&
        !this.state.isFetchingProductType
      ) {
        this.detectProductType(this.state.product);
      }

    }
  }

  mergeUniqueBloodBanks = (...bloodBanksLists) => {
    return bloodBanksLists
      .flatMap((entry) => {
        if (!entry) return [];
        return Array.isArray(entry) ? entry : [entry];
      })
      .filter((bloodBank, index, self) => {
        const bloodBankId = `${bloodBank?._id || bloodBank?.value || ""}`;

        return (
          bloodBankId !== "" &&
          index ===
            self.findIndex(
              (item) => `${item?._id || item?.value || ""}` === bloodBankId,
            )
        );
      });
  };

  loadAllowedDestinationsFromHospital = async (hospitalId) => {
    try {
      this.setState({ isFetchingDestinationBloodBanks: true });
      const hospitals = await hospitalQuery(this.props.defaultLanguage, {
        id: hospitalId,
      });
      const hospital = hospitals?.[0];
      if (!hospital) {
        // hospitalQuery returned nothing (no permission or not found) — fall back to props
        this.setState({
          source: this.props.from || { label: this.props.hospital?.name, value: null },
          bloodBank: { label: this.props.service?.name || this.props.hospital?.name },
          isFetchingDestinationBloodBanks: false,
        });
        return;
      }
      const allowed = this.mergeUniqueBloodBanks(
        hospital.allowedBloodBank,
        hospital.multipleAllowedBloodBank || []
      );
      const destOptions = allowed.filter(Boolean).map((bb) => ({
        ...bb,
        label: bb.name || bb.label,
        value: bb._id || bb.value,
      }));

      // hospital.bloodBank = the hospital's own blood bank (submission `to`)
      const hospitalOwnBB = hospital.bloodBank
        ? { label: hospital.bloodBank.name, value: hospital.bloodBank._id }
        : null;

      if (destOptions.length >= 1) {
        const autoSelected = destOptions[0];
        const sourceValue = hospitalOwnBB?.value || autoSelected.value;
        this.setState({
          destinationStocks: destOptions,
          nominativeDestination: autoSelected,
          source: { label: hospitalOwnBB?.label || this.props.hospital?.name, value: sourceValue },
          // bloodBank.value must always be set — fall back to first allowed BB when hospital has no own BB
          bloodBank: { value: hospitalOwnBB?.value || autoSelected.value, label: this.props.service?.name || this.props.hospital?.name },
          isFetchingDestinationBloodBanks: false,
        });
      } else {
        this.setState({
          source: { label: hospitalOwnBB?.label || this.props.hospital?.name, value: hospitalOwnBB?.value || null },
          bloodBank: { value: hospitalOwnBB?.value || null, label: this.props.service?.name || this.props.hospital?.name },
          isFetchingDestinationBloodBanks: false,
        });
      }
    } catch (error) {
      this.setState({ isFetchingDestinationBloodBanks: false });
    }
  };

  getPatientServices = async (isFetchingDestinationBloodBanks, search = {}) => {
    try {
      this.setState({ isFetchingDestinationBloodBanks });
      const data = await departmentQuery(this.props.defaultLanguage, {
        type: "dropdown",
        page: 1,
        limit: 100,
        ...search,
      });
      this.setState({
        serviceStocks: data || [],
        isFetchingDestinationBloodBanks: false,
      });
      return data || [];
    } catch (error) {
      this.setState({ isFetchingDestinationBloodBanks: false });
    }
  };

  getBloodBanks = async (isFetchingBloodBanks, search = {}) => {
    try {
      this.setState({
        isFetchingBloodBanks,
      });

      const { stock } = await defineRoleCenterSite();

      const data = await fetchBloodBanks(this.props.defaultLanguage, {
        page: 1,
        limit: 50,
        ...search,
        type: "dropdown",
      });

      let source,
        res = [];

      data &&
        data.forEach((el) => {
          if (stock?.includes(el._id)) {
            source = el;
            res.push(el);
            // Use 'return' to exit this iteration as we found a matching element
            return;
          }

          // If the element is marked as default and source is not set
          if (el.isDefault && !source) {
            source = el;
          }

          // Push the element to 'res' if it's not the source already added
          if (source !== el) {
            res.push(el);
          }
        });

      this.setState({
        isFetchingBloodBanks: false,
        sourceStocks: res,
        source,
      });

      return res;
    } catch (error) {
      this.setState({ isFetchingBloodBanks: false });
      toastMessage("error", error);
      this.onReset();
    }
  };

  getDestinationBloodBanks = async (
    isFetchingDestinationBloodBanks,
    search = {}
  ) => {
    try {
      this.setState({
        isFetchingDestinationBloodBanks,
      });

      let params = {
        page: 1,
        limit: 50,
        ...search,
        type: "dropdown",
        isDirectTransfer: true,
      };

     const { stock } = await defineRoleCenterSite();

     if (!search.stock) {
       params.allowedBloodBank = stock;
     }

      const data = await hospitalQuery(this.props.defaultLanguage, params);

      let res = [];

      data &&
        data.forEach((el) => {
          if (el?.bloodBank?._id) {
            res.push({
              label: el.bloodBank.name,
              value: el.bloodBank._id,
            });
          }
        });

      this.setState({
        isFetchingDestinationBloodBanks: false,
      });

      return res;
    } catch (error) {
      this.setState({ isFetchingDestinationBloodBanks: false });
    }
  };

  getShipmentNumber = async (isLoadingShipmentNumber) => {
    try {
      const { defaultLanguage } = this.props;

      this.setState({ isLoadingShipmentNumber });
      let data = null;
      if (!this.props.shipmentNumber) {
        data = await fetchShipmentNumber(defaultLanguage);
      }

      this.setState({
        shipmentNumber:
          this.props.shipmentNumber || generateShipmentNumber(data.data),
        isLoadingShipmentNumber: false,
        isSubmitting: false,
        isRefreshing: !this.state.isRefreshing
          ? !!this.props.shipmentNumber
          : this.state.isRefreshing,
      });
    } catch (error) {
      this.setState({
        isLoadingShipmentNumber: false,
        isSubmitting: false,
      });

      toastMessage("error", error);
    }
  };

  onChangeText = async (field, e) => {
    let { error } = this.state;
    let inputValue = e && e?.target ? e?.target?.value : e;

    delete error[field];

    this.setState({
      [field]: inputValue,
      isSearching: false,
      // Clear cached reservation info when the user changes donation/product
      // so the next Add click re-fetches for the new values.
      ...(field === "donationNumber" || field === "product"
        ? { reservationInfo: null }
        : {}),
    });

    if (field === "source") {
      this.getDestinationBloodBanks(true, {
        allowedBloodBank: inputValue?.value,
      });
    }

    if (field === "nominativeDestination") {
      this.setState({
        nominativeDestination: inputValue,
        source: { label: this.props.hospital?.name, value: inputValue?.value },
      });
    }

    if (field === "donationNumber" && this.state.isHospitalSource && inputValue) {
      this.autoDetectPatientService(inputValue);
    }

    if (field === "donationNumber") {
      // Reset crossmatch and product type detection whenever donation changes
      this.setState({ isRedBloodCell: null, isFetchingProductType: false, ...this._crossmatchReset() });
      if (inputValue.length === 7) {
        this.productInputRef.current?.focus();
      }
    }

    if (field === "product") {
      // Reset product type and crossmatch whenever product code changes
      this.setState({ isRedBloodCell: null, isFetchingProductType: false, ...this._crossmatchReset() });
      if (inputValue.length === 5) {
        this.donationInputRef.current?.focus();
        this.detectProductType(inputValue);
      }
    }
  };

  autoFillProduct = async (donationNumber) => {
    try {
      const donations = await bloodDonationQuery(this.props.defaultLanguage, { donationNumber });
      const donationId = donations?.[0]?._id;
      if (!donationId) {
        this.productInputRef.current?.focus();
        return;
      }

      const { data } = await fetchInventories(this.props.defaultLanguage, {
        donation: donationId,
        limit: 10,
        noStockFilter: true,
      });

      if (data?.length === 1) {
        const code = data[0].code;
        this.setState({ product: code }, () => {
          this.detectProductType(code);
          this.productInputRef.current?.focus();
        });
      } else {
        this.productInputRef.current?.focus();
      }
    } catch (_) {
      this.productInputRef.current?.focus();
    }
  };

  autoDetectPatientService = async (donationNumber) => {
    try {
      const { hospitalRole } = await defineRoleCenterSite();
      const donations = await bloodDonationQuery(this.props.defaultLanguage, { donationNumber });
      const donation = donations?.[0];
      if (!donation?.patient) return;

      const patientId = donation.patient?._id || donation.patient;
      const hospitalizations = await fetchPatientHospitalizations(
        this.props.defaultLanguage,
        { patient: patientId, hospital: hospitalRole, isActive: true, limit: 1 }
      );

      const active = Array.isArray(hospitalizations) ? hospitalizations[0] : hospitalizations?.data?.[0];
      if (!active?.service) return;

      const service = active.service;
      this.setState({
        bloodBank: { label: service.name, value: service._id },
      });
    } catch (_) {}
  };

  handleShowModal(modal, inventoryId) {
    this.setState({
      [modal]: true,
      inventoryId,
    });
  }

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
      inventoryId: null,
    });
  }

  onReset() {
    this.getShipmentNumber(true);

    this.setState({
      donationNumber: "",
      donation: {},
      product: "",
      isRedBloodCell: null,
      isFetchingProductType: false,
      ...this._crossmatchReset(),
    });
  }

  validateTransfer = () => {
    const { error, product, shipmentNumber, donationNumber } = this.state;

    if (!product) {
      error.product = "Product is required";
    }

    if (!donationNumber || donationNumber === "") {
      error.donationNumber = "Donation number is required";
    }

    if (shipmentNumber === "") {
      error.shipmentNumber = "Shipment number is required";
    }

    this.setState({
      error,
    });
  };

  // Called automatically when crossmatch confirms compatible.
  // Reserves the product to the patient (sets patient + RESERVED on the
  // inventory so the label shows patient info) then opens the label modal.
  // Blood usage (TRANSFUSION_PENDING) is NOT created here — that happens
  // at reception on the backend.
  generateLabelOnCompatible = async () => {
    if (!this.props.isForNominative) return;
    try {
      const { donationNumber, product } = this.state;
      const { defaultLanguage } = this.props;

      const { data } = await fetchInventories(defaultLanguage, {
        code: product,
        donationNumber,
        noStockFilter: true,
      });
      if (!data || data.length === 0) {
        toastMessage("error", language[defaultLanguage].no_data_found);
        return;
      }
      await this.onServeProduct(data[0]._id);
    } catch (error) {
      toastMessage("error", error);
    }
  };

  onServeProduct = async (inventoryId) => {
    try {
      const { requestId, patientId } = this.props;
      let method = "PUT",
        requestBody = {
          inventory: inventoryId,
          request: requestId,
          patient: patientId,
        };

      const options = {
        method,
        url: `${process.env.REACT_APP_BBMS_BASE_API}/inventory/patient`,
        data: requestBody,
      };
      await api(options);

      this.handleShowModal("showLabelModal", inventoryId);

      this.setState({ isSubmitting: false });
    } catch (error) {
      this.setState({ isSubmitting: false });
      const msg = error?.response?.data?.error || error?.message || String(error);
      // Product is held by another patient — show the reservation modal so
      // the user can release or reassign, rather than failing silently.
      if (
        msg.toLowerCase().includes("given to another patient") ||
        msg.toUpperCase().includes("RESERVED") ||
        msg.toUpperCase().includes("TRANSFUSION_PENDING")
      ) {
        this.fetchReservationInfo();
        return;
      }
      toastMessage("error", error);
    }
  };

  onSubmit = async () => {
    const {
      error,
      bloodBank,
      product,
      source,
      shipmentNumber,
      donationNumber,
    } = this.state;

    const { _id, requestNo, isForNominative, patientId } = this.props;

    if (!this.state.donation) return;

    // If we already fetched reservation info for this product/donation, just
    // re-open the modal rather than making another round-trip to the server.
    if (this.state.reservationInfo) {
      this.setState({ showReservationModal: true });
      return;
    }

    await this.validateTransfer();

    if (Object.keys(error)?.length > 0) return;

    try {
      this.setState({ isSubmitting: true, isRefreshing: false });

      let method = "POST",
        requestBody = {
          shipmentNumber,
          product,
          to: this.props.bloodBank || bloodBank?.value || this.props.to?._id || this.props.to?.value || null,
          from: this.props.source || source?.value || this.state.user?.stock || this.props.from?._id || this.props.from?.value || null,
          donationNumber,
          requestNo,
          isForNominative: this.props.isForNominative,
          patient: patientId,
          service: this.props.service?._id || null,
        };

      if (_id && _id === "") {
        method = "PUT";
        requestBody.id = _id;
      }

      const options = {
        method,
        url: `${process.env.REACT_APP_BBMS_BASE_API}/shipping-queue`,
        data: requestBody,
      };

      await api(options);

      toastMessage(
        "success",
        language[this.props.defaultLanguage].add_data_success
      );

      this.setState({
        isSubmitting: false,
        isRefreshing: true,
        donationNumber: "",
        product: "",
        isRedBloodCell: null,
        isFetchingProductType: false,
        ...this._crossmatchReset(),
      });
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || String(error);

      if (msg.toUpperCase().includes("RESERVED") || msg.toUpperCase().includes("TRANSFUSION_PENDING")) {
        this.setState({ isSubmitting: false, isRefreshing: false });
        this.fetchReservationInfo();
        return;
      }

      toastMessage("error", error);

      this.setState({
        isSubmitting: false,
        isRefreshing: false,
        ...this._crossmatchReset(),
      });
    }
  };

  fetchReservationInfo = async () => {
    try {
      const { donationNumber, product } = this.state;
      const { data } = await api({
        method: "GET",
        url: `${process.env.REACT_APP_BBMS_BASE_API}/shipping-queue/reservation-info`,
        params: { donationNumber, code: product },
      });
      if (!data) {
        toastMessage("error", "Product reservation details could not be found. It may have already been freed.");
        return;
      }
      this.setState({ reservationInfo: data, showReservationModal: true });
    } catch (_) {
      toastMessage("error", "Could not load reservation details.");
    }
  };

  handleCancelReservation = (action) => {
    this.setState({ pendingAction: action });
  };

  confirmCancelReservation = async () => {
    const { pendingAction, donationNumber, product } = this.state;
    const { patientId, requestId } = this.props;

    this.setState({ isCancellingReservation: true });
    try {
      await api({
        method: "POST",
        url: `${process.env.REACT_APP_BBMS_BASE_API}/shipping-queue/cancel-reservation`,
        data: {
          donationNumber,
          code: product,
          ...(pendingAction === "reassign" && {
            newPatient: patientId,
            newRequest: requestId,
          }),
        },
      });

      this.setState({
        isCancellingReservation: false,
        pendingAction: null,
        showReservationModal: false,
        reservationInfo: null,
      });

      if (pendingAction === "reassign") {
        // Product is now assigned to current patient — generate the label so
        // the user can print it, then click Add to submit the shipment.
        await this.generateLabelOnCompatible();
      } else {
        toastMessage("success", "Reservation cancelled. Product is now back in stock.");
      }
    } catch (error) {
      this.setState({ isCancellingReservation: false });
      toastMessage("error", error);
    }
  };

  detectProductType = async (productCode) => {
    if (!productCode || productCode.length < 5) return;
    try {
      this.setState({ isFetchingProductType: true, isRedBloodCell: null });

      let componentName = "";
      const { donationNumber } = this.state;

      // If donation number is ready, fetch the specific inventory (most reliable)
      if (donationNumber && donationNumber.length >= 7) {
        const donations = await bloodDonationQuery(this.props.defaultLanguage, { donationNumber });
        const donationId = donations?.[0]?._id;
        if (donationId) {
          const { data } = await fetchInventories(this.props.defaultLanguage, {
            code: productCode,
            donation: donationId,
            limit: 1,
            noStockFilter: true,
          });
          componentName = (data?.[0]?.component?.name || "").toLowerCase();
          // Only taken from the donation-specific lookup. The fallback
          // below matches any unit carrying this component code, so its
          // group belongs to some other bag and comparing it would warn
          // about the wrong thing.
          this.setState({
            scannedUnit: data?.[0]
              ? {
                  bloodGroup: data[0].bloodGroup,
                  componentName: data[0].component?.name,
                }
              : null,
          });
        }
      }

      // Fallback: search any inventory with this component code
      if (!componentName) {
        const { data } = await fetchInventories(this.props.defaultLanguage, {
          code: productCode,
          limit: 1,
          noStockFilter: true,
        });
        componentName = (data?.[0]?.component?.name || "").toLowerCase();
      }

      const isRBC =
        componentName.includes("red blood") ||
        componentName.includes("rbc") ||
        componentName.includes("erythrocyte");

      this.setState({ isRedBloodCell: isRBC, isFetchingProductType: false });
    } catch (_) {
      this.setState({ isRedBloodCell: false, isFetchingProductType: false });
    }
  };

  handleCompatibilityToggle = async () => {
    try {
      this.setState({ isCheckingBloodGroup: true });
      const { data } = await api({
        method: "POST",
        url: `${process.env.REACT_APP_BBMS_BASE_API}/bloodRequest/is-compatible-with-patient`,
        data: {
          donationNumber: this.state.donationNumber,
          patient: this.props.patientId,
          requestId: this.props.requestId,
          product: this.state.product,
          crossmatchResults: [],
        },
      });
      toastMessage("success", data?.message || "Product confirmed compatible");
      this.setState({ isCheckingBloodGroup: false, isCompatibleWithPatient: true }, () => {
        this.generateLabelOnCompatible();
      });
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        (typeof error === "string" ? error : error?.message) ||
        "Blood group incompatible";
      // RESERVED is a stock issue, not a blood group problem.
      // Show compatibility success, enable Add — label skipped since the
      // product belongs to another patient's reservation.
      if (msg.toUpperCase().includes("RESERVED")) {
        toastMessage("success", "Product confirmed compatible");
        this.setState({ isCheckingBloodGroup: false, isCompatibleWithPatient: true });
        return;
      }
      this.setState({ isCheckingBloodGroup: false });
      toastMessage("error", msg);
    }
  };

  _crossmatchReset = () => ({
    isCompatibleWithPatient: false,
    scannedUnit: null,
    crossmatchPhases: { IS: null, LISS: null, IAT: null, CC: null },
    crossmatchVerdict: null,
    crossmatchCurrentPhaseIndex: 0,
    isCheckingBloodGroup: false,
    bloodGroupStatus: null,
    bloodGroupMessage: "",
  });

  // Called after CC Positive to confirm blood group compatibility via API.
  // On success → COMPATIBLE + enable Add. On fail → NOT COMPATIBLE with reason.
  confirmBloodGroupAfterCrossmatch = async (crossmatchResults) => {
    try {
      this.setState({ isCheckingBloodGroup: true });
      const { data } = await api({
        method: "POST",
        url: `${process.env.REACT_APP_BBMS_BASE_API}/bloodRequest/is-compatible-with-patient`,
        data: {
          donationNumber: this.state.donationNumber,
          patient: this.props.patientId,
          requestId: this.props.requestId,
          product: this.state.product,
          crossmatchResults,
        },
      });
      toastMessage("success", "Crossmatch complete — product confirmed compatible");
      this.setState({
        isCheckingBloodGroup: false,
        crossmatchVerdict: "compatible",
        bloodGroupMessage: data?.message || "",
        isCompatibleWithPatient: true,
      }, () => {
        this.generateLabelOnCompatible();
      });
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error === "string" ? error : error?.message) ||
        "Blood group incompatible";
      if (msg.toUpperCase().includes("RESERVED")) {
        toastMessage("success", "Crossmatch complete — product confirmed compatible");
        this.setState({
          isCheckingBloodGroup: false,
          crossmatchVerdict: "compatible",
          isCompatibleWithPatient: true,
        });
        return;
      }
      this.setState({
        isCheckingBloodGroup: false,
        crossmatchVerdict: "not_compatible",
        bloodGroupMessage: msg,
      });
      toastMessage("error", error);
    }
  };

  setCrossmatchPhaseResult = async (phaseId, result) => {
    if (result === this.state.crossmatchPhases[phaseId]) return;

    const phaseIndex = CROSSMATCH_PHASES.findIndex((p) => p.id === phaseId);
    const phase = CROSSMATCH_PHASES[phaseIndex];
    const isFail = result === phase.failLabel;

    // CC Negative means the IAT technique was invalid — reset from IAT and ask to redo
    if (phase.id === "CC" && isFail) {
      const iatIndex = CROSSMATCH_PHASES.findIndex((p) => p.id === "IAT");
      const resetPhases = { ...this.state.crossmatchPhases };
      CROSSMATCH_PHASES.forEach((p, i) => { if (i >= iatIndex) resetPhases[p.id] = null; });
      this.setState({ crossmatchPhases: resetPhases, crossmatchCurrentPhaseIndex: iatIndex, crossmatchVerdict: null });
      toastMessage("error", "Check Cells Negative — IAT technique invalid. Please redo the IAT phase.");
      return;
    }

    // Set this phase's result and clear all subsequent phases so sequence stays valid
    const updatedPhases = { ...this.state.crossmatchPhases, [phaseId]: result };
    CROSSMATCH_PHASES.forEach((p, i) => { if (i > phaseIndex) updatedPhases[p.id] = null; });

    let verdict = null;
    let crossmatchCurrentPhaseIndex = phaseIndex;

    if (isFail) {
      verdict = "not_compatible";
      toastMessage("error", "NOT COMPATIBLE — This product must NOT be transfused to this patient.");
    } else if (phaseIndex === CROSSMATCH_PHASES.length - 1) {
      verdict = "compatible";
    } else {
      crossmatchCurrentPhaseIndex = phaseIndex + 1;
    }

    this.setState({
      crossmatchPhases: updatedPhases,
      crossmatchVerdict: verdict,
      crossmatchCurrentPhaseIndex,
      isCompatibleWithPatient: false,
    });

    if (verdict === "compatible") {
      const crossmatchResults = CROSSMATCH_PHASES.map((p) => ({ phase: p.id, result: updatedPhases[p.id] }));
      await this.confirmBloodGroupAfterCrossmatch(crossmatchResults);
    }
  };

  resetCrossmatchFrom = (phaseId) => {
    const phaseIndex = CROSSMATCH_PHASES.findIndex((p) => p.id === phaseId);
    const updatedPhases = { ...this.state.crossmatchPhases };
    CROSSMATCH_PHASES.forEach((p, i) => {
      if (i >= phaseIndex) updatedPhases[p.id] = null;
    });
    this.setState({
      crossmatchPhases: updatedPhases,
      crossmatchCurrentPhaseIndex: phaseIndex,
      crossmatchVerdict: null,
      isCompatibleWithPatient: false,
    });
  };

  renderCrossmatch = () => {
    const {
      isCheckingBloodGroup,
      crossmatchPhases,
      crossmatchCurrentPhaseIndex,
      crossmatchVerdict,
      isCompatibleWithPatient,
    } = this.state;

    return (
      <div>
        <Separator
          title="Compatibility Testing — Crossmatch"
          titleClassName="text-primary"
        />

        {/* Manual phases — two per row */}
        <div className="row g-2">
          {CROSSMATCH_PHASES.map((phase, i) => {
            const result = crossmatchPhases[phase.id];
            const isDone = result !== null;
            const isActive = i === crossmatchCurrentPhaseIndex && !crossmatchVerdict && !isCheckingBloodGroup;
            const isPassed = result === phase.passLabel;

            const options = [
              { value: phase.passLabel, label: phase.passLabel },
              { value: phase.failLabel, label: phase.failLabel },
            ];
            const selectedValue = result ? { value: result, label: result } : null;

            return (
              <div key={phase.id} className="col-6">
                <Select
                  label={phase.label ? `${phase.abbr} — ${phase.label}` : phase.abbr}
                  required
                  options={options}
                  value={selectedValue}
                  onChange={(option) => this.setCrossmatchPhaseResult(phase.id, option?.value)}
                  placeholder="Select result"
                  disabled={isCompatibleWithPatient || (!isDone && !isActive) || isCheckingBloodGroup}
                  success={isDone && isPassed}
                  error={isDone && !isPassed ? " " : undefined}
                  helperText={phase.description}
                />
              </div>
            );
          })}
        </div>

        {/* Blood group confirmation spinner — shown after CC Positive while API runs */}
        {isCheckingBloodGroup && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: "0.5rem 0", padding: "0.5rem 0.75rem", background: "#e8f0fe", borderRadius: 6 }}>
            <Loading />
            <span style={{ fontSize: 12, color: "#1a3a6b" }}>Confirming blood group compatibility…</span>
          </div>
        )}

      </div>
    );
  };

  render() {
    return (
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-md-5">
        {!this.props.sampleNotCollected && (
          <form>
              <div className="row mb-3">
                {(!this.props.source || this.props.source === "") && (
                  <div className="col-md-6">
                    {this.props.isForNominative ? (
                      <Input
                        label={language[this.props.defaultLanguage].source}
                        value={this.state.source?.label || ""}
                        leftIcon={icons.source}
                        disabled
                      />
                    ) : (
                      <Select
                        options={this.state.sourceStocks}
                        placeholder={language[this.props.defaultLanguage].select}
                        label={language[this.props.defaultLanguage].source}
                        value={this.state.source}
                        onChange={(e) => this.onChangeText("source", e)}
                        error={this.state.error.source}
                        leftIcon={icons.source}
                        disabled={!!this.state.user.stock || !!this.props._id}
                      />
                    )}
                  </div>
                )}
                {this.props.isForNominative ? (
                  <div className="col-md-6">
                    <Input
                      label={language[this.props.defaultLanguage].destination}
                      value={this.state.bloodBank?.label || ""}
                      leftIcon={icons.bloodBank}
                      disabled
                    />
                  </div>
                ) : this.props.service?._id ? (
                  <Input
                    label={language[this.props.defaultLanguage].destination}
                    value={this.props?.service?.name}
                    disabled
                  />
                ) : this.state.isHospitalSource ? (
                  (!this.props.bloodBank || this.props.bloodBank === "") && (
                    <div className="col-md-6">
                      <Select
                        options={this.state.serviceStocks}
                        placeholder={language[this.props.defaultLanguage].select}
                        label={language[this.props.defaultLanguage].destination}
                        value={this.state.bloodBank}
                        onChange={(e) => this.onChangeText("bloodBank", e)}
                        error={this.state.error.bloodBank}
                        leftIcon={icons.bloodBank}
                        isLoading={this.state.isFetchingDestinationBloodBanks}
                        disabled={this.props._id}
                      />
                    </div>
                  )
                ) : (
                  (!this.props.bloodBank || this.props.bloodBank === "") && (
                    <div className="col-md-6">
                      <Select
                        loadOptions={(e) =>
                          this.getDestinationBloodBanks(true, {
                            q: e,
                          })
                        }
                        placeholder={
                          language[this.props.defaultLanguage].select
                        }
                        label={language[this.props.defaultLanguage].destination}
                        value={this.state.bloodBank}
                        onChange={(e) => this.onChangeText("bloodBank", e)}
                        error={this.state.error.bloodBank}
                        leftIcon={icons.bloodBank}
                        isLoading={this.state.isFetchingDestinationBloodBanks}
                        disabled={this.props._id}
                      />
                    </div>
                  )
                )}
              </div>
              <Input
                placeholder={
                  language[this.props.defaultLanguage]
                    .shipment_number_placeholder
                }
                label={language[this.props.defaultLanguage].shipment_number}
                value={this.state.shipmentNumber}
                onChange={(e) => this.onChangeText("shipmentNumber", e)}
                error={this.state.error.shipmentNumber}
                disabled
              />
              <Input
                placeholder={
                  language[this.props.defaultLanguage]
                    .donation_number_placeholder
                }
                label={language[this.props.defaultLanguage].donation_number}
                value={this.state.donationNumber}
                onChange={(e) => this.onChangeText("donationNumber", e)}
                error={this.state.error.donationNumber}
                leftIcon={icons.user}
                inputRef={this.donationInputRef}
              />
              <div className="col-md-12">
                <Input
                  placeholder={
                    language[this.props.defaultLanguage].product_placeholder
                  }
                  label={language[this.props.defaultLanguage].product}
                  value={this.state.product}
                  onChange={(e) => this.onChangeText("product", e)}
                  error={this.state.error.product}
                  leftIcon={icons.product}
                  inputRef={this.productInputRef}
                  type="number"
                />
              </div>
              {this.props.isForNominative && (
                <div className="col-md-12" style={{ marginBottom: "0.5rem" }}>
                  {this.state.isFetchingProductType ? (
                    <Loading />
                  ) : skipsCrossmatch(this.props.request) ? (
                    /* The doctor asked for uncrossmatched or untested
                       units, or there is no sample to test against. The
                       panel stands down rather than asking the laboratory
                       for results it cannot produce - but the unit is still
                       checked against the patient's group, so waiving the
                       testing cannot quietly become a wrong-unit error. */
                    <EmergencyIssueCheck
                      request={this.props.request}
                      scannedUnit={this.state.scannedUnit}
                      confirmed={this.state.isCompatibleWithPatient}
                      onConfirm={() =>
                        this.setState({ isCompatibleWithPatient: true })
                      }
                    />
                  ) : this.state.isRedBloodCell === true ? (
                    this.renderCrossmatch()
                  ) : this.state.isRedBloodCell === false ? (
                    <>
                      <div
                        onClick={!this.state.isCompatibleWithPatient ? this.handleCompatibilityToggle : undefined}
                        style={{ cursor: !this.state.isCompatibleWithPatient ? "pointer" : "default" }}
                      >
                        <Switch
                          label={language[this.props.defaultLanguage].compatible_with_patient}
                          checked={this.state.isCompatibleWithPatient}
                        />
                      </div>
                      <br />
                    </>
                  ) : null}
                </div>
              )}
              {/* {!this.state?.donation?._id && (
              <Button
                text={language[this.props.defaultLanguage].search}
                isSubmitting={this.state.isSearching}
                onPress={(e) => {
                  e.preventDefault();
                  this.handleSearch();
                }}
              />
            )} */}
              <div className="d-flex gap-2 align-items-center">
                <Button
                  text={language[this.props.defaultLanguage].reset}
                  onPress={(e) => {
                    e.preventDefault();
                    this.onReset();
                  }}
                  className="btn-default"
                  type="button"
                />
                {this.props.isForNominative && this.state.isCompatibleWithPatient && this.state.isRedBloodCell && (
                  <Button
                    text={language[this.props.defaultLanguage].edit_tests || "Edit Tests"}
                    icon="edit"
                    onPress={(e) => {
                      e.preventDefault();
                      this.setState(this._crossmatchReset());
                    }}
                    type="button"
                  />
                )}
                {this.props.isForNominative ? (
                  <Button
                    text={language[this.props.defaultLanguage].add}
                    isSubmitting={this.state.isSubmitting}
                    onPress={(e) => {
                      e.preventDefault();
                      this.onSubmit();
                    }}
                    disabled={!this.state.isCompatibleWithPatient}
                  />
                ) : (
                  <Button
                    text={language[this.props.defaultLanguage].add}
                    isSubmitting={this.state.isSubmitting}
                    onPress={(e) => {
                      e.preventDefault();
                      this.onSubmit();
                    }}
                  />
                )}
              </div>
            </form>
        )}
            </div>

            <div className="col-md-7">
              <ShippingQueue
                shipmentNumber={this.state.shipmentNumber}
                requestNo={this.props.requestNo}
                isRefreshing={this.state.isRefreshing}
                addedBy={this.state?.user?._id}
                isForNominative={this.props.isForNominative}
              />
            </div>
          </div>
        </div>

        {!!this.state?.inventoryId && (
          <Modal
            handleClose={this.handleCloseModal.bind(this, "showLabelModal")}
            show={this.state.showLabelModal}
            showHeaderBottomBorder={false}
          >
            <PrintableLabel
              handleCloseModal={this.handleCloseModal.bind(
                this,
                "showLabelModal"
              )}
              productId={this.state.inventoryId}
            />
          </Modal>
        )}

        {/* Reservation info modal */}
        <Modal
          handleClose={() => this.setState({ showReservationModal: false, pendingAction: null })}
          show={this.state.showReservationModal}
          size="lg"
          title={
            this.state.reservationInfo?.openUsage
              ? `Product ${this.state.product} with Donation number ${this.state.donationNumber} — Transfusion Pending`
              : `Product ${this.state.product} with Donation number ${this.state.donationNumber} — Reserved`
          }
        >
          {this.state.reservationInfo && (() => {
            const { inventory, shipment, openUsage } = this.state.reservationInfo;
            const reservedPatient = openUsage?.patient || inventory?.patient;
            const reservedRequest = inventory?.request;
            const isTransfusionPending = !!openUsage;
            const reservedName = reservedPatient
              ? `${reservedPatient.firstName} ${reservedPatient.lastName} (ID: ${reservedPatient.patientId})`
              : "another patient";

            const infoRows = [
              reservedPatient && { label: "Patient", value: <strong>{reservedPatient.firstName} {reservedPatient.lastName}</strong> },
              reservedPatient && { label: "Patient ID", value: reservedPatient.patientId },
              reservedPatient && { label: "Blood Group", value: reservedPatient.bloodGroup },
              isTransfusionPending && {
                label: "Transfusion Status",
                value: <span className="badge badge-cancelled" style={{ fontSize: 11 }}>{openUsage.status}</span>,
              },
              reservedRequest?.requestNo && { label: "Request No", value: <strong>{reservedRequest.requestNo}</strong> },
              reservedRequest?.service?.name && { label: "Service", value: reservedRequest.service.name },
              shipment?.from?.name && { label: "Reserved at", value: shipment.from.name },
            ].filter(Boolean);

            return (
              <div className="px-3 px-md-4 py-3">

                {/* Warning banner */}
                <div className="d-flex align-items-start gap-3 mb-4 p-3" style={{
                  backgroundColor: "var(--error-color-10, #fdf0f0)",
                  borderLeft: "4px solid var(--error-color)",
                  borderRadius: "var(--border-radius)",
                }}>
                  <span className="material-symbols-outlined flex-shrink-0" style={{ color: "var(--error-color)", fontSize: 22, marginTop: 1 }}>warning</span>
                  <div>
                    <p className="text-bold mb-1" style={{ color: "var(--error-color)", fontSize: 15 }}>
                      {isTransfusionPending ? "Active Transfusion In Progress" : "Product Already Reserved"}
                    </p>
                    <p className="mb-0" style={{ fontSize: "var(--font-size)", lineHeight: 1.5 }}>
                      {isTransfusionPending
                        ? <>This product has an active <strong>TRANSFUSION_PENDING</strong> record for <strong>{reservedName}</strong>. It cannot be re-dispatched without first resolving this usage.</>
                        : <>This product is currently reserved for <strong>{reservedName}</strong> and cannot be dispatched to another patient without cancelling the existing reservation.</>}
                    </p>
                  </div>
                </div>

                {/* Section: patient */}
                <p className="text-bold mb-2" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-color-60)" }}>
                  {isTransfusionPending ? "Patient with Active Transfusion Record" : "Patient This Product Is Reserved For"}
                </p>
                <div className="table-responsive mb-4" style={{ border: "1px solid var(--text-color-10)", borderRadius: "var(--border-radius)", overflow: "hidden" }}>
                  <table className="table table-sm mb-0" style={{ tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: "38%" }} />
                      <col style={{ width: "62%" }} />
                    </colgroup>
                    <tbody>
                      {infoRows.map((row, i) => (
                        <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "var(--primary-color-5, #f9f9f9)" : "transparent" }}>
                          <td style={{ color: "var(--text-color-60)", fontSize: "var(--font-size)", padding: "8px 12px", borderRight: "1px solid var(--text-color-10)", verticalAlign: "middle" }}>
                            {row.label}
                          </td>
                          <td style={{ padding: "8px 12px", fontSize: "var(--font-size)", verticalAlign: "middle", wordBreak: "break-word" }}>
                            {row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Section: actions or inline confirm */}
                {this.state.pendingAction ? (() => {
                  const isReassign = this.state.pendingAction === "reassign";
                  return (
                    <div className="p-3" style={{
                      border: `2px solid ${isReassign ? "var(--primary-color)" : "var(--error-color)"}`,
                      borderRadius: "var(--border-radius)",
                      background: isReassign ? "var(--primary-color-5, #f9f4f4)" : "var(--error-color-10, #fdf0f0)",
                    }}>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="material-symbols-outlined" style={{ color: isReassign ? "var(--primary-color)" : "var(--error-color)", fontSize: 20 }}>
                          {isReassign ? "person_add" : "undo"}
                        </span>
                        <span className="text-bold" style={{ fontSize: 15 }}>
                          {isReassign ? "Confirm Reassignment" : "Confirm Cancellation"}
                        </span>
                      </div>
                      <p className="mb-3" style={{ fontSize: "var(--font-size)", lineHeight: 1.6 }}>
                        {isReassign
                          ? <>This will <strong>remove the reservation</strong> from <strong>{reservedName}</strong> and assign this product to <strong>{this.props.patientName || "the patient on this request"}</strong>. A new label will be generated automatically.<br />Proceed only after clinical team confirmation.</>
                          : <>This will <strong>cancel the reservation</strong> for <strong>{reservedName}</strong> and return the product to <strong>{this.state.source?.label || "stock"}</strong>. The previous patient will no longer have this product reserved.</>}
                      </p>
                      <div className="d-flex flex-wrap gap-2 justify-content-end">
                        <Button
                          className="btn-default border"
                          text="← Go Back"
                          onPress={() => this.setState({ pendingAction: null })}
                          disabled={this.state.isCancellingReservation}
                        />
                        <Button
                          className={isReassign ? "btn-primary" : "btn-danger"}
                          text={isReassign ? "Yes, Reassign & Generate Label" : "Yes, Cancel Reservation"}
                          isSubmitting={this.state.isCancellingReservation}
                          onPress={this.confirmCancelReservation}
                        />
                      </div>
                    </div>
                  );
                })() : (
                  <>
                    <p className="text-bold mb-3" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-color-60)" }}>
                      Choose an Action
                    </p>
                    <div className="row g-3 mb-3">
                      {/* Release to stock */}
                      <div className={this.props.patientId ? "col-12 col-sm-6" : "col-12"}>
                        <div className="h-100 d-flex flex-column p-3" style={{ border: "1px solid var(--text-color-10)", borderRadius: "var(--border-radius)" }}>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span className="material-symbols-outlined" style={{ color: "var(--error-color)", fontSize: 18 }}>undo</span>
                            <span className="text-bold" style={{ fontSize: 14 }}>Release to Stock</span>
                          </div>
                          <p className="mb-3 flex-grow-1" style={{ fontSize: "var(--font-size)", lineHeight: 1.5 }}>
                            Cancels the reservation for <strong>{reservedName}</strong> and returns this product to <strong>{this.state.source?.label || "stock"}</strong>. Use when no immediate reassignment is needed.
                          </p>
                          <Button
                            className="btn-danger"
                            text="Cancel Reservation (→ INSTOCK)"
                            onPress={() => this.handleCancelReservation("free")}
                          />
                        </div>
                      </div>
                      {/* Reassign */}
                      {this.props.patientId && (
                        <div className="col-12 col-sm-6">
                          <div className="h-100 d-flex flex-column p-3" style={{ border: "1px solid var(--primary-color, #5c1e1e)", borderRadius: "var(--border-radius)" }}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <span className="material-symbols-outlined" style={{ color: "var(--primary-color)", fontSize: 18 }}>person_add</span>
                              <span className="text-bold" style={{ fontSize: 14 }}>Reassign to Current Patient</span>
                            </div>
                            <p className="mb-3 flex-grow-1" style={{ fontSize: "var(--font-size)", lineHeight: 1.5 }}>
                              Removes from <strong>{reservedName}</strong> and assigns directly to <strong>{this.props.patientName || "the patient on this request"}</strong>. A new label will be generated automatically. Proceed only after clinical team confirmation.
                            </p>
                            <Button
                              className="btn-primary"
                              text="Reassign & Generate Label"
                              onPress={() => this.handleCancelReservation("reassign")}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {!this.state.pendingAction && (
                  <div style={{ background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: 6, padding: "8px 12px", marginBottom: 12, display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 16, lineHeight: 1.4 }}>⚠</span>
                    <span style={{ fontSize: 12, color: "#92400e", lineHeight: 1.5 }}>
                      <strong>Label reminder:</strong> After any action above, the existing label on the blood bag must be removed before the product is dispatched to another patient.
                    </span>
                  </div>
                )}
                {!this.state.pendingAction && (
                  <div className="d-flex justify-content-end pt-2" style={{ borderTop: "1px solid var(--text-color-10)" }}>
                    <Button
                      className="btn-default border"
                      text="Close"
                      onPress={() => this.setState({ showReservationModal: false })}
                    />
                  </div>
                )}
              </div>
            );
          })()}
        </Modal>

        {/* Floating guide button */}
        {this.props.isForNominative && (
          <div
            style={{ position: "fixed", bottom: 0, right: 0, margin: "1rem", zIndex: 1050 }}
            onClick={() => this.setState({ showGuide: true })}
          >
            <div
              style={{
                backgroundColor: "var(--primary-color)",
                color: "white",
                padding: ".5rem 1rem .5rem .5rem",
                borderRadius: "1.5rem",
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: ".5rem",
                border: "2px solid var(--primary-color)",
              }}
            >
              <span style={{ color: "white" }} className={process.env.REACT_APP_ICONS_TYPE}>
                {icons.help}
              </span>
              <span className="text-bold">Need help? Use this guide</span>
            </div>
          </div>
        )}

        {/* Transfer Product Guide Modal */}
        <Modal
          handleClose={() => this.setState({ showGuide: false })}
          show={this.state.showGuide}
          title="How to Transfer a Product — Nominative Requests"
          size="lg"
        >
          <div className="card-body" style={{ fontSize: 14, lineHeight: 1.7 }}>

            {/* Step-by-step */}
            <p className="text-bold mb-2" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 1, color: "var(--primary-color)" }}>
              Step-by-step process
            </p>
            <ol style={{ paddingLeft: 20, marginBottom: 24 }}>
              <li><strong>Enter the Donation Number</strong> of the product you want to dispatch to the patient.</li>
              <li><strong>Enter the Product code</strong> (e.g. 04210, 12000).</li>
              <li>The system will run a <strong>crossmatch compatibility check</strong> against the patient's blood group. Wait for the result.</li>
              <li>If the result is <span style={{ color: "green", fontWeight: "bold" }}>Compatible</span>, the toggle turns ON and the <strong>Add</strong> button becomes active.</li>
              <li>Click <strong>Add</strong> to create the shipment and generate the patient label.</li>
              <li>Print the label and attach it to the blood bag before dispatching.</li>
            </ol>

            {/* Reserved product */}
            <p className="text-bold mb-2" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 1, color: "var(--error-color)" }}>
              What to do when a product is already reserved
            </p>
            <p className="mb-2">
              When you click <strong>Add</strong> and the system shows a <em>"Transfusion Pending"</em> or <em>"Reserved"</em> alert, it means the product is already assigned to another patient. You have two options:
            </p>

            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-6">
                <div style={{ border: "1px solid var(--error-color)", borderRadius: 8, padding: 16 }}>
                  <p className="text-bold mb-1" style={{ color: "var(--error-color)" }}>Option 1 — Release to Stock</p>
                  <p className="mb-2" style={{ fontSize: 13 }}>
                    Cancels the existing reservation and returns the product to the blood bank stock. The product becomes available for any patient.
                  </p>
                  <p className="mb-0" style={{ fontSize: 12, color: "var(--text-color-50)" }}>
                    <strong>Use when:</strong> The original patient no longer needs the product, the reservation was made in error, or clinical staff have confirmed the change.
                  </p>
                </div>
              </div>
              <div className="col-12 col-sm-6">
                <div style={{ border: "1px solid var(--primary-color)", borderRadius: 8, padding: 16 }}>
                  <p className="text-bold mb-1" style={{ color: "var(--primary-color)" }}>Option 2 — Reassign to Current Patient</p>
                  <p className="mb-2" style={{ fontSize: 13 }}>
                    Removes the reservation from the original patient and immediately assigns this product to the current request's patient. A new label is generated automatically.
                  </p>
                  <p className="mb-0" style={{ fontSize: 12, color: "var(--text-color-50)" }}>
                    <strong>Use when:</strong> The clinical team has decided to redirect this specific product. Always obtain team confirmation before reassigning.
                  </p>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div style={{ background: "var(--warning-color-10, #fff8e1)", border: "1px solid #f59e0b", borderRadius: 8, padding: 14, marginBottom: 16 }}>
              <p className="text-bold mb-1" style={{ color: "#92400e" }}>
                ⚠ Important — Clinical Responsibility
              </p>
              <ul style={{ paddingLeft: 18, marginBottom: 0, fontSize: 13 }}>
                <li>Never cancel or reassign a reservation without approval from the clinical team.</li>
                <li>Both actions directly affect patient care and transfusion safety.</li>
                <li>After reassigning, ensure the new label is printed and correctly attached to the bag.</li>
                <li>Document all changes in the patient's transfusion record.</li>
              </ul>
            </div>

            {/* Crossmatch reminder */}
            <p className="text-bold mb-1" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>
              Crossmatch results
            </p>
            <ul style={{ paddingLeft: 18, fontSize: 13, marginBottom: 0 }}>
              <li><strong style={{ color: "green" }}>Compatible</strong>: Blood groups match. Safe to proceed with the transfer.</li>
              <li><strong style={{ color: "var(--error-color)" }}>Not Compatible</strong>: Blood groups do not match. Do NOT proceed — select a different product.</li>
              <li>For Red Blood Cells (RBC), always perform and record all crossmatch phases before dispatching.</li>
            </ul>

          </div>
        </Modal>

      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  const { refreshShipmentNumber } = state.ShipmentNumber;
  return {
    defaultLanguage,
    refreshShipmentNumber,
  };
};

export default connect(mapStateToProps)(DirectTransfer);
